import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { petitionsAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageSquare, Loader2, Send, User, CheckCircle, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

export default function PetitionsPage() {
  const { canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [total, setTotal] = useState(0);
  const pg = usePagination(scope);

  const fetchItems = useCallback(() => {
    setLoading(true);
    petitionsAPI.list(scope === 'all' ? undefined : scope, pg.params)
      .then(res => { setItems(res.data?.data || []); setTotal(res.data?.total || 0); })
      .catch(err => toast.error(hubError(err, 'Error al cargar peticiones')))
      .finally(() => setLoading(false));
  }, [scope, pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const selectedItem = items.find(i => i.id === selectedId);

  const handleReply = async () => {
    if (!replyText.trim() || !selectedId) return;
    setSending(true);
    try {
      await petitionsAPI.respond(selectedId, replyText);
      toast.success('Respuesta enviada');
      fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al responder')); } finally { setSending(false); }
  };

  const selectPetition = (item) => {
    setSelectedId(item.id);
    setReplyText(item.pastor_response || '');
  };

  return (
    <div>
      <TopBar title="Peticiones" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Tabs value={scope} onValueChange={setScope} className="mb-4">
          <TabsList>
            <TabsTrigger value="all" data-testid="petitions-filter-all">Todas</TabsTrigger>
            <TabsTrigger value="community" data-testid="petitions-filter-community">Comunidad</TabsTrigger>
            <TabsTrigger value="private" data-testid="petitions-filter-private">Privadas</TabsTrigger>
          </TabsList>
        </Tabs>

        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="peticiones" testId="petitions-pagination"
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><MessageSquare className="w-12 h-12 text-muted-foreground/40" /><p>No hay peticiones</p></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[500px]">
            <div className="lg:col-span-5">
              <ScrollArea className="h-[600px] lg:h-[calc(100vh-240px)]">
                <div className="space-y-2 pr-2">
                  {items.map(item => {
                    const answered = !!item.pastor_response;
                    const isSelected = selectedId === item.id;
                    return (
                      <Card
                        key={item.id}
                        className={`cursor-pointer transition-colors ${isSelected ? 'ring-2 ring-[#00CCFF] bg-accent/50' : 'hover:bg-accent/30'}`}
                        onClick={() => selectPetition(item)}
                        data-testid={`petition-item-${item.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-[#00CCFF]/10 flex items-center justify-center text-xs font-medium text-[#056A84]">
                                {item.is_anonymous ? '?' : <User className="w-4 h-4" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{item.is_anonymous ? 'Anónimo' : (item.author_name || 'Miembro')}</p>
                                <p className="text-[10px] text-muted-foreground">{item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : ''}</p>
                              </div>
                            </div>
                            {answered ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-yellow-500" />}
                          </div>
                          {item.title && <p className="font-medium text-sm mt-2">{item.title}</p>}
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{item.content}</p>
                          <div className="flex items-center gap-2 mt-2">
                            {item.visibility === 'private' && <Badge variant="outline" className="text-[10px]"><Lock className="w-2.5 h-2.5 mr-1" />Privada</Badge>}
                            {answered && <Badge variant="secondary" className="text-[10px]">Respondida</Badge>}
                            {typeof item.pray_count === 'number' && <span className="text-[10px] text-muted-foreground">{item.pray_count} oraron</span>}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            <div className="lg:col-span-7">
              {selectedItem ? (
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {selectedItem.is_anonymous ? 'Anónimo' : (selectedItem.author_name || 'Miembro')}
                        </span>
                        <Badge variant={selectedItem.pastor_response ? 'default' : 'secondary'} className="text-[10px]">
                          {selectedItem.pastor_response ? 'Respondida' : 'Pendiente'}
                        </Badge>
                      </div>
                      {selectedItem.title && <h3 className="text-lg font-semibold">{selectedItem.title}</h3>}
                      <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{selectedItem.content}</p>
                    </div>
                    {canUpdate('petitions') ? (
                      <div className="border-t pt-4 mt-4">
                        <Label className="text-sm font-medium mb-2 block">Respuesta pastoral</Label>
                        <Textarea
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Escribe tu respuesta..."
                          rows={4}
                          className="mb-3"
                          data-testid="petitions-reply-input"
                        />
                        <Button
                          className="bg-[#056A84] hover:bg-[#045A70] text-white"
                          onClick={handleReply}
                          disabled={sending || !replyText.trim()}
                          data-testid="petitions-reply-send-button"
                        >
                          {sending ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                          Enviar respuesta
                        </Button>
                      </div>
                    ) : selectedItem.pastor_response && (
                      <div className="border-t pt-4 mt-4">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Respuesta pastoral</p>
                        <p className="text-sm">{selectedItem.pastor_response}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full">
                  <CardContent className="p-6 flex flex-col items-center justify-center min-h-[400px] text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">Selecciona una petición para ver los detalles y responder</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
